import * as THREE from 'three';

export function probeFloatRt(renderer: THREE.WebGLRenderer): boolean {
  const gl = renderer.getContext() as WebGL2RenderingContext | WebGLRenderingContext;
  if (!gl || typeof (gl as WebGL2RenderingContext).texStorage2D !== 'function') return false;
  if (!gl.getExtension('EXT_color_buffer_float')) return false;
  const tex = gl.createTexture();
  const fb = gl.createFramebuffer();
  if (!tex || !fb) return false;
  const prevTex = gl.getParameter(gl.TEXTURE_BINDING_2D);
  const prevFb = gl.getParameter(gl.FRAMEBUFFER_BINDING);
  try {
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      (gl as WebGL2RenderingContext).RGBA32F,
      1,
      1,
      0,
      gl.RGBA,
      gl.FLOAT,
      null,
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    return gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
  } catch {
    return false;
  } finally {
    gl.bindFramebuffer(gl.FRAMEBUFFER, prevFb);
    gl.bindTexture(gl.TEXTURE_2D, prevTex);
    gl.deleteFramebuffer(fb);
    gl.deleteTexture(tex);
  }
}

export function makeFloatTarget(
  size: number,
  filter: THREE.MagnificationTextureFilter,
): THREE.WebGLRenderTarget {
  const target = new THREE.WebGLRenderTarget(size, size, {
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
    minFilter: filter,
    magFilter: filter,
    format: THREE.RGBAFormat,
    type: THREE.FloatType,
    depthBuffer: false,
    stencilBuffer: false,
  });
  target.texture.generateMipmaps = false;
  return target;
}

export function makeHalfTarget(
  size: number,
  filter: THREE.MagnificationTextureFilter,
): THREE.WebGLRenderTarget {
  const target = new THREE.WebGLRenderTarget(size, size, {
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
    minFilter: filter,
    magFilter: filter,
    format: THREE.RGBAFormat,
    type: THREE.HalfFloatType,
    depthBuffer: false,
    stencilBuffer: false,
  });
  target.texture.generateMipmaps = false;
  return target;
}

export function webglTexture(
  renderer: THREE.WebGLRenderer,
  texture: THREE.Texture,
): WebGLTexture | null {
  const props = renderer.properties.get(texture) as { __webglTexture?: WebGLTexture } | undefined;
  return props?.__webglTexture ?? null;
}

export function uploadFull(
  renderer: THREE.WebGLRenderer,
  target: THREE.WebGLRenderTarget,
  size: number,
  data: Float32Array,
): void {
  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(target);
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const tex = webglTexture(renderer, target.texture);
  if (tex) {
    const prevTex = gl.getParameter(gl.TEXTURE_BINDING_2D);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, size, size, gl.RGBA, gl.FLOAT, data);
    gl.bindTexture(gl.TEXTURE_2D, prevTex);
  }
  renderer.setRenderTarget(prev);
}

export function uploadSpans(
  renderer: THREE.WebGLRenderer,
  target: THREE.WebGLRenderTarget,
  data: Float32Array,
  spans: Array<{ x: number; y: number; n: number; offset: number }>,
): void {
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const tex = webglTexture(renderer, target.texture);
  if (!tex) return;
  const prevTex = gl.getParameter(gl.TEXTURE_BINDING_2D);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  for (const span of spans) {
    const slice = data.subarray(span.offset * 4, (span.offset + span.n) * 4);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, span.x, span.y, span.n, 1, gl.RGBA, gl.FLOAT, slice);
  }
  gl.bindTexture(gl.TEXTURE_2D, prevTex);
}

export function clearTarget(
  renderer: THREE.WebGLRenderer,
  target: THREE.WebGLRenderTarget,
  color: THREE.Color | number = 0,
): void {
  const prev = renderer.getRenderTarget();
  const prevAuto = renderer.autoClear;
  renderer.autoClear = true;
  renderer.setClearColor(color, 0);
  renderer.setRenderTarget(target);
  renderer.clear();
  renderer.setRenderTarget(prev);
  renderer.autoClear = prevAuto;
}
